import React, { useMemo } from 'react';
import { TopEventsPanel } from '../../components/dashboard/TopEventsPanel';
import { PageSummaryStats } from '../../components/dashboard/PageSummaryStats';
import { DashboardInsightChip } from '../../components/dashboard/DashboardInsightChip';
import { ConcentrationStrip } from '../../components/dashboard/ConcentrationStrip';
import ErrorBoundary from '../../components/ErrorBoundary';
import { SkeletonChart } from '../../components/ui/SkeletonBlock';
import { buildEventSummaryStats } from '../../lib/pageSummaryStats';
import { buildConcentrationRows, buildEventsInsight } from '../../lib/dashboardInsights';

export function EventsPage({
  committedFilters,
  topEventRows,
  isTopEventsLoading,
  isRefreshing,
  topEventsError,
  topEventsComparison,
  isFirstLoad,
  currencyCode = 'DKK',
  currencyUnavailable = false,
  filterSlot = null,
}) {
  const queryKey = committedFilters.queryString || '';
  const eventStats = useMemo(
    () =>
      buildEventSummaryStats(topEventRows, topEventsComparison, {
        currencyCode,
        currencyUnavailable,
      }),
    [topEventRows, topEventsComparison, currencyCode, currencyUnavailable]
  );
  const summaryError = topEventsError?.message || topEventsError || null;
  const insight = useMemo(
    () => buildEventsInsight(topEventRows, currencyCode),
    [topEventRows, currencyCode]
  );
  const concentration = useMemo(
    () => buildConcentrationRows(topEventRows, 'eventName'),
    [topEventRows]
  );

  return (
    <section className="flex w-full min-w-0 flex-col gap-5">
      <PageSummaryStats
        stats={eventStats}
        isFirstLoad={isFirstLoad}
        error={summaryError}
        emptyMessage="No event data found for this period. Try a different date range or filter."
      />

      {filterSlot}

      {!isFirstLoad && !summaryError && insight ? (
        <DashboardInsightChip tone={insight.tone} title={insight.title} detail={insight.detail} />
      ) : null}

      {isFirstLoad ? (
        <div className="grid gap-4 xl:grid-cols-3">
          <div className="xl:col-span-2"><SkeletonChart height="h-[320px]" /></div>
          <SkeletonChart height="h-[320px]" />
        </div>
      ) : (
        <div className="grid items-start gap-4 xl:grid-cols-3">
          <div className="min-w-0 xl:col-span-2">
            <ErrorBoundary key={`top-${queryKey}`}>
              <TopEventsPanel
                rows={topEventRows}
                isLoading={isTopEventsLoading || isRefreshing}
                error={topEventsError}
                comparison={topEventsComparison}
                filters={committedFilters}
                variant="detailed"
                currencyCode={currencyCode}
              />
            </ErrorBoundary>
          </div>
          <ConcentrationStrip title="Event concentration" rows={concentration} />
        </div>
      )}
    </section>
  );
}
